// Tutor DashboardPage — topbar, 4 stat cards, then a two-column layout:
// left = upcoming sessions + recent check-ins, right = my courses + a
// roster snapshot of recently enrolled students.
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth.js";
import { useFetch, getJson } from "../../hooks/useApi.js";
import { useCourses } from "../../contexts/CoursesContext.js";
import { useSessions } from "../../contexts/SessionsContext.js";
import Badge from "../../components/shared/Badge.js";
import Avatar from "../../components/shared/Avatar.js";
import PaymentStatusBadge from "../../components/shared/PaymentStatusBadge.js";
import AttendanceStatusBadge from "../../components/shared/AttendanceStatusBadge.js";
import StatCard from "../../components/shared/StatCard.js";
import { IconCalendarEvent, IconUserCheck, IconBook, IconUsers } from "../../components/shared/icons.js";
import { formatShortDateTime } from "../../lib/formatDateTime.js";
import type {
  AttendanceRecordWithStudent,
  EnrollmentsByCoursesResult,
  EnrollmentWithStudent,
  UnpaidStudentsSummary,
} from "@dm-lms/shared/types";

function formatTopbarDate(date: Date): string {
  return date.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "short", year: "numeric" });
}

export default function DashboardPage() {
  const { user } = useAuth();

  const { courses: myCourses, isLoading: coursesLoading, error: coursesError } = useCourses();
  const { sessions: sessionsData, isLoading: sessionsLoading } = useSessions();

  // One count for all of the tutor's courses (not a roster fetch per course); an alert is a
  // nicety, so a failed load simply shows no alert.
  const { data: unpaid } = useFetch<UnpaidStudentsSummary>("/api/enrollments/unpaid-count");
  const unpaidStudents = unpaid?.unpaidStudents ?? 0;

  const myCourseIds = useMemo(() => new Set(myCourses.map((course) => course.id)), [myCourses]);

  // GET /api/sessions already scopes to the tutor's own courses server-side
  // for a tutor caller — this filter is a harmless no-op safety net, same
  // as the existing tutor SessionsPage.
  const mySessions = useMemo(
    () => sessionsData.filter((session) => myCourseIds.has(session.courseId)),
    [sessionsData, myCourseIds]
  );

  const upcomingSessions = useMemo(() => {
    const now = Date.now();
    return mySessions
      .filter((session) => new Date(session.scheduledAt).getTime() > now)
      .sort((a, b) => new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime());
  }, [mySessions]);

  const sessionsThisMonth = useMemo(() => {
    const today = new Date();
    return mySessions.filter((session) => {
      const scheduled = new Date(session.scheduledAt);
      return scheduled.getFullYear() === today.getFullYear() && scheduled.getMonth() === today.getMonth();
    }).length;
  }, [mySessions]);

  const mostRecentPastSession = useMemo(() => {
    const now = Date.now();
    const past = mySessions.filter((session) => new Date(session.scheduledAt).getTime() <= now);
    if (past.length === 0) return null;
    return past.reduce((latest, session) =>
      new Date(session.scheduledAt).getTime() > new Date(latest.scheduledAt).getTime() ? session : latest
    );
  }, [mySessions]);

  const { data: recentAttendance, isLoading: attendanceLoading } = useFetch<AttendanceRecordWithStudent[]>(
    mostRecentPastSession ? `/api/attendance/session/${mostRecentPastSession.id}` : null
  );

  // Enrollments across every one of the tutor's courses — fetched once in a
  // single bulk round trip (bounded by how many courses a tutor has, not by
  // student count) and reused for the "My students" stat, each course's
  // enrolled count on the My courses card, and the Recent enrollments roster
  // snapshot. Avoids a separate /api/tutors/:id/stats call that would just be
  // a second, redundant source of the same numbers.
  const [allEnrollments, setAllEnrollments] = useState<EnrollmentWithStudent[]>([]);
  const [enrolledCountByCourse, setEnrolledCountByCourse] = useState<Map<string, number>>(new Map());
  const [uniqueStudentCount, setUniqueStudentCount] = useState(0);
  const [enrollmentsLoading, setEnrollmentsLoading] = useState(false);

  useEffect(() => {
    if (myCourses.length === 0) {
      setAllEnrollments([]);
      setEnrolledCountByCourse(new Map());
      setUniqueStudentCount(0);
      return;
    }
    let cancelled = false;
    setEnrollmentsLoading(true);

    const courseIds = myCourses.map((course) => course.id).join(",");
    void getJson<EnrollmentsByCoursesResult>(`/api/enrollments/courses?courseIds=${courseIds}`).then((res) => {
      if (cancelled) return;
      if (res.ok) {
        setAllEnrollments(res.data.items);
        setEnrolledCountByCourse(new Map(Object.entries(res.data.countsByCourse)));
        setUniqueStudentCount(res.data.uniqueStudentCount);
      }
      setEnrollmentsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [myCourses]);

  // Server-sorted newest-first (see GET /api/enrollments/courses) — no
  // client-side re-sort needed for the top-4 slice shown here.
  const recentEnrollments = useMemo(() => allEnrollments.slice(0, 4), [allEnrollments]);

  const courseTitleById = useMemo(() => {
    const map = new Map<string, string>();
    for (const course of myCourses) map.set(course.id, course.title);
    return map;
  }, [myCourses]);

  return (
    <div className="dm-page">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-dm-navy">Welcome back, {user?.name ?? "Tutor"}</h1>
        <p className="text-sm text-dm-slate">{formatTopbarDate(new Date())}</p>
      </div>

      {coursesLoading && <p className="text-dm-slate text-sm">Loading dashboard…</p>}
      {coursesError && <p className="text-red-600 text-sm">{coursesError}</p>}

      {!coursesLoading && !coursesError && (
        <>
          {unpaidStudents > 0 && (
            <div role="alert" className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
              <p className="font-semibold">
                ⚠ {unpaidStudents} enrolled {unpaidStudents === 1 ? "student has" : "students have"} pending or
                unverified payments.
              </p>
              <p className="mt-1">These students may not have completed payment yet.</p>
              <Link to="/app/tutor/roster" className="mt-2 inline-block font-medium text-dm-blue hover:underline">
                View roster →
              </Link>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard label="My courses" value={myCourses.length} />
            <StatCard label="My students" value={enrollmentsLoading ? "…" : uniqueStudentCount} />
            <StatCard label="Upcoming sessions" value={upcomingSessions.length} />
            <StatCard label="Sessions this month" value={sessionsThisMonth} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* LEFT COLUMN */}
            <div className="space-y-4">
              <div className="rounded-lg border border-gray-200 bg-white p-5">
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="flex items-center gap-2 text-sm font-medium text-dm-navy">
                    <IconCalendarEvent width={16} height={16} className="text-dm-slate" />
                    Upcoming sessions
                  </h2>
                  <Link to="/app/tutor/sessions" className="text-xs font-medium text-dm-blue hover:underline">
                    View all
                  </Link>
                </div>

                {sessionsLoading && <p className="text-sm text-dm-slate">Loading…</p>}

                {!sessionsLoading && upcomingSessions.length === 0 && (
                  <p className="text-sm text-dm-slate">
                    No upcoming sessions.{" "}
                    <Link to="/app/tutor/sessions" className="font-medium text-dm-blue hover:underline">
                      Create one from the Sessions page.
                    </Link>
                  </p>
                )}

                {!sessionsLoading && upcomingSessions.length > 0 && (
                  <div className="divide-y divide-gray-100">
                    {upcomingSessions.slice(0, 4).map((session) => (
                      <div key={session.id} className="flex items-center gap-3 py-2.5">
                        <span className="h-2 w-2 shrink-0 rounded-full bg-blue-500" />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-[13px] font-semibold text-dm-navy">{session.title}</p>
                          <p className="truncate text-[11px] text-dm-slate">
                            {session.courseTitle} · {formatShortDateTime(session.scheduledAt)}
                          </p>
                        </div>
                        <Link
                          to={`/app/tutor/sessions/${session.id}`}
                          className="shrink-0 rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-dm-navy hover:bg-gray-50"
                        >
                          View
                        </Link>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="rounded-lg border border-gray-200 bg-white p-5">
                <h2 className="mb-3 flex items-center gap-2 text-sm font-medium text-dm-navy">
                  <IconUserCheck width={16} height={16} className="text-dm-slate" />
                  Recent check-ins
                </h2>

                {!mostRecentPastSession && !sessionsLoading && (
                  <p className="text-sm text-dm-slate">No sessions run yet.</p>
                )}

                {mostRecentPastSession && attendanceLoading && <p className="text-sm text-dm-slate">Loading…</p>}

                {mostRecentPastSession && !attendanceLoading && (recentAttendance?.length ?? 0) === 0 && (
                  <p className="text-sm text-dm-slate">No attendance recorded for the last session yet.</p>
                )}

                {mostRecentPastSession && !attendanceLoading && (recentAttendance?.length ?? 0) > 0 && (
                  <>
                    <div className="divide-y divide-gray-100">
                      {recentAttendance!.slice(0, 5).map((record) => (
                        <div key={record.id} className="flex items-center justify-between gap-3 py-2.5">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium text-dm-navy">{record.studentName}</p>
                            <p className="truncate text-xs text-dm-slate">{record.studentCode}</p>
                          </div>
                          <div className="flex shrink-0 items-center gap-3">
                            <AttendanceStatusBadge status={record.status} />
                            <span className="text-xs text-dm-slate">
                              {record.checkedInAt ? formatShortDateTime(record.checkedInAt) : "—"}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                    <Link
                      to="/app/tutor/attendance"
                      className="mt-3 inline-block text-xs font-medium text-dm-blue hover:underline"
                    >
                      View full attendance
                    </Link>
                  </>
                )}
              </div>
            </div>

            {/* RIGHT COLUMN */}
            <div className="space-y-4">
              <div className="rounded-lg border border-gray-200 bg-white p-5">
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="flex items-center gap-2 text-sm font-medium text-dm-navy">
                    <IconBook width={16} height={16} className="text-dm-slate" />
                    My courses
                  </h2>
                  <Link to="/app/tutor/courses" className="text-xs font-medium text-dm-blue hover:underline">
                    View all
                  </Link>
                </div>

                {myCourses.length === 0 ? (
                  <p className="text-sm text-dm-slate">No courses assigned yet. Contact admin.</p>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {myCourses.slice(0, 3).map((course) => (
                      <div key={course.id} className="flex items-center justify-between gap-3 py-2.5">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-dm-navy">
                            {course.code} · {course.title}
                          </p>
                          <div className="mt-1 flex items-center gap-2">
                            <Badge tone="gray">{course.mode}</Badge>
                            <span className="text-xs text-dm-slate">
                              {enrollmentsLoading ? "…" : (enrolledCountByCourse.get(course.id) ?? 0)} enrolled
                            </span>
                          </div>
                        </div>
                        <Link
                          to={`/app/tutor/roster?courseId=${course.id}`}
                          className="shrink-0 rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-dm-navy hover:bg-gray-50"
                        >
                          View roster
                        </Link>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="rounded-lg border border-gray-200 bg-white p-5">
                <h2 className="mb-3 flex items-center gap-2 text-sm font-medium text-dm-navy">
                  <IconUsers width={16} height={16} className="text-dm-slate" />
                  Recent enrollments
                </h2>

                {enrollmentsLoading && <p className="text-sm text-dm-slate">Loading…</p>}

                {!enrollmentsLoading && recentEnrollments.length === 0 && (
                  <p className="text-sm text-dm-slate">No students enrolled yet.</p>
                )}

                {!enrollmentsLoading && recentEnrollments.length > 0 && (
                  <>
                    <div className="divide-y divide-gray-100">
                      {recentEnrollments.map((enrollment) => (
                        <div key={enrollment.id} className="flex items-center justify-between gap-3 py-2.5">
                          <div className="flex min-w-0 items-center gap-3">
                            <Avatar name={enrollment.studentName} />
                            <div className="min-w-0">
                              <p className="truncate text-sm font-medium text-dm-navy">{enrollment.studentName}</p>
                              <p className="truncate text-xs text-dm-slate">
                                {enrollment.studentCode} · {courseTitleById.get(enrollment.courseId) ?? "—"}
                              </p>
                            </div>
                          </div>
                          <div className="shrink-0 text-right">
                            <PaymentStatusBadge status={enrollment.paymentStatus} />
                            <p className="mt-1 text-xs text-dm-slate">{formatShortDateTime(enrollment.enrolledAt)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <Link
                      to="/app/tutor/roster"
                      className="mt-3 inline-block text-xs font-medium text-dm-blue hover:underline"
                    >
                      View full roster
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
